import React, { use, useMemo } from "react";
import { useNavigation } from "@react-navigation/native";
import { CompositeScreenProps } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import {
    ClientStatus,
    ContractSignatureStatus,
    KycStatus,
    RejectedByType,
} from "../__generated__/graphql";
import { AuthManagerContext } from "../contexts/AuthManager";
import { useClientSigningStatusPolling } from "../hooks/use-client-signing-status-polling";
import { useCurrentProfile } from "../hooks/use-current-profile";
import { useI18n } from "../hooks/i18n-hooks";
import { useIntercom } from "../hooks/intercom";
import { useTheme } from "../hooks/use-theme";
import { RootStackParamList } from "../navigations/RootNavigation";
import { ProfileStackParamList } from "../navigations/ProfileStack";
import AlertBox from "../ui_atoms/AlertBox";
import VStack from "../ui_atoms/VStack";
import HStack from "../ui_atoms/HStack";
import Gap from "../ui_atoms/Gap";
import Button from "./Button";
import { BodyMediumText } from "./FormattedText";

type ScreenParams = CompositeScreenProps<
    StackScreenProps<RootStackParamList>,
    StackScreenProps<ProfileStackParamList>
>;

type ClientWarning = {
    messageKey: string;
    messageParams?: Record<string, string>;
    showAssistanceButton?: boolean;
    showClientContract?: boolean;
};

const EmployerProfileWarnings: React.FC = () => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { user } = use(AuthManagerContext);
    const currentProfile = useCurrentProfile();
    const navigator = useNavigation<ScreenParams["navigation"]>();
    const { presentMessageComposer } = useIntercom();

    const client = currentProfile && "client" in currentProfile ? currentProfile.client : undefined;
    const useLseCct = !!client?.useLegalEmployer?.useLseCct;

    useClientSigningStatusPolling(client?.status, useLseCct);

    const needKyc = useMemo(
        () => user?.kycStatus?.status !== KycStatus.Accepted,
        [user?.kycStatus?.status],
    );

    const clientWarning = useMemo<ClientWarning | null>(() => {
        if (!client) return null;
        const status = client.status;
        const rejectedBy = client.rejectedBy;
        const rejectedReason = client.rejectedReason ?? "";
        const signatureStatus = client.signatureStatus;

        if (useLseCct) {
            if (status === ClientStatus.Accepted) return null;
            if (status === ClientStatus.Rejected) {
                return {
                    messageKey: "profile.warnings.client_refused",
                    messageParams: { rejectedReason },
                    showAssistanceButton: true,
                };
            }
            return { messageKey: "profile.warnings.client_not_verified" };
        }

        if (status === ClientStatus.Signed) return null;
        if (status === ClientStatus.Rejected) {
            if (rejectedBy === RejectedByType.User) {
                return {
                    messageKey: "profile.warnings.client_reject",
                    messageParams: { rejectedReason },
                    showAssistanceButton: true,
                };
            }
            return {
                messageKey: "profile.warnings.client_refused",
                messageParams: { rejectedReason },
                showAssistanceButton: true,
            };
        }
        if (signatureStatus === ContractSignatureStatus.NeedSeal) {
            return { messageKey: "profile.warnings.client_not_verified" };
        }
        if (signatureStatus === ContractSignatureStatus.HandwrittenInProcess) {
            return { messageKey: "profile.warnings.client_handwritten_in_process" };
        }
        return {
            messageKey: "profile.warnings.client_not_verified_no_lse",
            showClientContract: true,
        };
    }, [client, useLseCct]);

    if (!needKyc && !clientWarning) return null;
    if (!client) return null;

    return (
        <VStack alignment="stretch">
            {needKyc && (
                <>
                    <AlertBox mode="warning">
                        <VStack v={1 / 2} gap={1 / 2} style={{ flex: 1 }} alignment="stretch">
                            <BodyMediumText style={{ color: colors.onWarningBackground }}>
                                {i18n.t("profile.warnings.kyc_required")}
                            </BodyMediumText>
                            <HStack fit="center">
                                <Button
                                    style={{ flex: 1 }}
                                    label={i18n.t("profile.warnings.launch_kyc")}
                                    onPress={() =>
                                        navigator.navigate("PreIdVerification", {
                                            backOnSkip: true,
                                        })
                                    }
                                />
                            </HStack>
                        </VStack>
                    </AlertBox>
                    <Gap gap={1} />
                </>
            )}
            {!needKyc && clientWarning && (
                <>
                    <AlertBox mode="warning">
                        <VStack v={1 / 2} gap={1 / 2} style={{ flex: 1 }} alignment="stretch">
                            <BodyMediumText style={{ color: colors.onWarningBackground }}>
                                {i18n.t(clientWarning.messageKey, clientWarning.messageParams)}
                            </BodyMediumText>
                            {clientWarning.showClientContract ? (
                                <HStack fit="center">
                                    <Button
                                        style={{ flex: 1 }}
                                        label={i18n.t("profile.warnings.show_client_contract")}
                                        onPress={() => {
                                            navigator.navigate("ContractPreviewScreen", {
                                                contractType: "client_framework",
                                                contractId: client.id,
                                            });
                                        }}
                                    />
                                </HStack>
                            ) : (
                                clientWarning.showAssistanceButton && (
                                    <HStack fit="center">
                                        <Button
                                            style={{ flex: 1 }}
                                            label={i18n.t("profile.warnings.contact_support")}
                                            onPress={() => void presentMessageComposer()}
                                        />
                                    </HStack>
                                )
                            )}
                        </VStack>
                    </AlertBox>
                    <Gap gap={1} />
                </>
            )}
        </VStack>
    );
};

export default EmployerProfileWarnings;
